<template>
  <div
    data-testid="tm-results-summary-v2"
    class="tm-results-summary-v2"
  >
    <!-- Toolbar: Date Filter -->
    <div class="summary-v2__toolbar" data-testid="tm-results-summary-v2__toolbar">
      <div class="summary-v2__toolbar-left">
        <!-- Date filter hidden but logic retained -->
        <div v-show="false">
          <span class="summary-v2__as-at-label">As at</span>

          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290"
            data-testid="tm-results-summary-v2__date-menu"
          >
            <template v-slot:activator="{ on, attrs }">
              <button
                class="summary-v2__date-btn"
                v-bind="attrs"
                v-on="on"
                data-testid="tm-results-summary-v2__date-btn"
              >
                <v-icon small class="summary-v2__date-btn-icon">mdi-calendar</v-icon>
                <span :class="selectedDate ? 'summary-v2__date-btn-text--active' : 'summary-v2__date-btn-text--placeholder'">
                  {{ selectedDate ? $nova.formatDate(selectedDate) : 'Select Date' }}
                </span>
              </button>
            </template>

            <v-date-picker
              v-model="selectedDate"
              no-title
              :min="minDateAllowed"
              :max="maxDateAllowed"
              @input="onDateSelected"
              color="primary"
            />
          </v-menu>
        </div>
      </div>

      <v-spacer />

      <DownloadExcel
        button-text="Generate Excel"
        button-color="white"
        test-id-prefix="tm-results-summary-v2__download-excel"
        :download-history="filteredDownloadHistory"
        :loading-history="loadingHistory"
        :current-user-id="$store.state.user.id"
        :compact="true"
        @generate="handleGenerateExcel"
        @refresh="$emit('refresh-download-history')"
        @download="$emit('download-history-file', $event)"
      />
    </div>

    <!-- Loading State (initial load, no data yet) -->
    <div
      v-if="loading && groupedTables.length === 0"
      class="summary-v2__loading"
      data-testid="tm-results-summary-v2__loading-state"
    >
      <img
        src="/img/loader_new.svg"
        alt="Loading"
        class="summary-v2__loading-img"
        data-testid="tm-results-summary-v2__loading-image"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="groupedTables.length === 0"
      class="summary-v2__empty"
      data-testid="tm-results-summary-v2__empty-state"
    >
      No summary data available
    </div>

    <!-- Grouped Tables -->
    <div
      v-else
      class="summary-v2__content"
      data-testid="tm-results-summary-v2__content"
    >
      <div
        v-for="group in groupedTables"
        :key="group.key"
        class="summary-v2__group"
        :data-testid="'tm-results-summary-v2__group-' + group.key"
      >
        <div class="summary-v2__card">
          <table class="summary-v2__table">
            <thead>
              <!-- Card title row -->
              <tr class="summary-v2__title-row">
                <th
                  :colspan="displayHeaders.length"
                  class="summary-v2__title-cell"
                >
                  {{ group.title }}
                </th>
              </tr>
              <!-- Column header row -->
              <tr class="summary-v2__header-row">
                <th
                  v-for="(header, hi) in displayHeaders"
                  :key="header"
                  class="summary-v2__th"
                  :class="{
                    'summary-v2__th--right': isNumericColumn(header),
                    'summary-v2__th--border-right': hi < displayHeaders.length - 1
                  }"
                >
                  {{ formatHeaderDisplay(header) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, ri) in group.rows"
                :key="row._id || ri"
                class="summary-v2__row"
                :class="{ 'summary-v2__row--alt': ri % 2 !== 0 }"
                data-testid="tm-results-summary-v2__data-row"
              >
                <td
                  v-for="(header, hi) in displayHeaders"
                  :key="header"
                  class="summary-v2__td"
                  :class="{
                    'summary-v2__td--number': isNumericValue(row[header]),
                    'summary-v2__td--border-right': hi < displayHeaders.length - 1
                  }"
                >
                  {{ formatCell(row[header]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Overall footer with pagination -->
      <div
        class="summary-v2__table-footer"
        data-testid="tm-results-summary-v2__footer"
      >
        <span class="summary-v2__record-count">
          <strong>{{ totalCount }}</strong>&nbsp;total records
        </span>

        <v-spacer></v-spacer>

        <!-- Pagination -->
        <div
          v-if="pageCount > 1"
          data-testid="tm-results-summary-v2__pagination"
          class="d-flex align-center ga-2"
        >
          <v-btn
            icon
            small
            data-testid="tm-results-summary-v2__prev-btn"
            :disabled="page === 1"
            @click="handlePageChange(page - 1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span>Page {{ page }} of {{ pageCount }}</span>
          <v-btn
            icon
            small
            data-testid="tm-results-summary-v2__next-btn"
            :disabled="page === pageCount"
            @click="handlePageChange(page + 1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * TMResultsSummaryV2 Component
 *
 * Displays the Summary (Matching matrix) tab for V2 Transaction Matching results.
 * Self-contained: loads its own data from the V3 annexure API.
 *
 * Data is grouped by unique (fileConfig1, fileConfig2) pairs — order-insensitive,
 * so (A, B) and (B, A) are treated as the same group.
 * Each group renders as a separate titled table.
 *
 * Features:
 * - Date filter ("As at") matching the Report tab pattern
 * - Grouped tables by fileConfig pair with resolved display names
 * - Formatted numbers and clean column headers
 * - API-driven pagination
 *
 * @component
 */

import { mapActions } from 'vuex';
import { formatHeaderText } from '../shared/tmResultsDataTableUtils.js';
import DownloadExcel from '../TransationMatching/downloadExcel.vue';

/** Columns excluded from display (internal / grouping fields) */
const HIDDEN_COLUMNS = new Set([
  '_id', 'fileConfig1', 'fileConfig2', 'created_at', 'updated_at', 'index', 'pair_index'
]);

export default {
  name: 'TMResultsSummaryV2',

  components: {
    DownloadExcel
  },

  props: {
    /** The MongoDB collection name for the summary annexure. */
    collection: {
      type: String,
      required: true
    },

    /** Analysis details object containing bpid, _id, etc. */
    analysisDetails: {
      type: Object,
      default: () => ({})
    },

    /** Single V2 rundetail object. */
    runDetails: {
      type: Object,
      default: () => ({})
    },

    /** File configuration info keyed by fileConfig ID. */
    fileConfigInfo: {
      type: Object,
      default: () => ({})
    },

    /**
     * Array of file config objects from setup.file.
     * Each has { _id, name, ... }. Used to resolve IDs → names in headers.
     */
    fileList: {
      type: Array,
      default: () => []
    },
    /** Array of download history files for DownloadExcel dropdown. */
    downloadHistory: {
      type: Array,
      default: () => []
    },
    /** Loading state for download history. */
    loadingHistory: {
      type: Boolean,
      default: false
    },
    /** Original annexure tab name (e.g. "Summary"). */
    tabName: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      summaryData: [],
      tableHeaders: [],
      page: 1,
      limit: 50,
      totalCount: 0,
      pageCount: 0,
      dateMenu: false,
      selectedDate: null
    };
  },

  computed: {
    /** Filter download history to only show entries matching this tab. */
    filteredDownloadHistory() {
      if (!this.tabName) return this.downloadHistory;
      return this.downloadHistory.filter(
        file => !Array.isArray(file.tables) || file.tables.length === 0 || file.tables.includes(this.tabName)
      );
    },

    /**
     * Minimum date allowed in the date picker.
     * Uses startdate from analysisDetails.
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    minDateAllowed() {
      return this.analysisDetails?.startdate || null;
    },

    /**
     * Maximum date allowed in the date picker.
     * Uses the greater of enddate from analysisDetails or today's date.
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    maxDateAllowed() {
      const today = this.$nova.formatDateISO(new Date());
      const enddate = this.analysisDetails?.enddate;
      
      if (!enddate) return today;
      return enddate > today ? enddate : today;
    },

    /**
     * Build the filter payload for the API.
     * When a date is selected, filters by the "date" column.
     * @returns {Object}
     */
    dateFilterPayload() {
      if (!this.selectedDate) return { groups: [], group_connectors: [] };
      return {
        groups: [
          {
            connector: 'AND',
            conditions: [
              { column: 'date', operator: 'eq', value: this.selectedDate }
            ]
          }
        ],
        group_connectors: []
      };
    },

    /**
     * Headers to display — excludes internal/grouping columns.
     * @returns {string[]}
     */
    displayHeaders() {
      return this.tableHeaders.filter(h => !HIDDEN_COLUMNS.has(h));
    },

    /**
     * Map of fileConfig ID → display name.
     * Built primarily from fileList (setup.file) which has the human-readable names.
     * Falls back to fileConfigInfo keys if fileList is not available.
     * @returns {Object<string, string>}
     */
    fileConfigNameMap() {
      const map = {};

      // Primary source: fileList (setup.file array) — has _id and name
      if (this.fileList && this.fileList.length > 0) {
        for (const file of this.fileList) {
          if (file._id) {
            map[file._id] = file.name || file.alias || file._id;
          }
        }
      }

      // Fallback: fileConfigInfo (setup.fileconfig object)
      if (this.fileConfigInfo) {
        for (const [id, config] of Object.entries(this.fileConfigInfo)) {
          if (!map[id]) {
            map[id] = config.name || config.label || config.filename || id;
          }
        }
      }

      return map;
    },

    /**
     * Group records by unique (fileConfig1, fileConfig2) pair.
     * Pairs are order-insensitive: (A,B) and (B,A) are the same group.
     * Groups are sorted by fileList position: T1↔T2, T1↔T3, T2↔T3.
     * Each group has { key, title, rows }.
     * @returns {Array<{key: string, title: string, rows: Array}>}
     */
    groupedTables() {
      if (!this.summaryData || this.summaryData.length === 0) return [];

      // Build position map from fileList order (T1=0, T2=1, T3=2 …)
      const fileListOrder = {};
      (this.fileList || []).forEach((f, i) => {
        if (f._id) fileListOrder[f._id] = i;
      });

      const groupMap = new Map();

      for (const record of this.summaryData) {
        const fc1 = record.fileConfig1 || '';
        const fc2 = record.fileConfig2 || '';

        // Order the pair by fileList position so (T2,T1) is treated same as (T1,T2)
        const idx1 = fileListOrder[fc1] ?? Infinity;
        const idx2 = fileListOrder[fc2] ?? Infinity;
        const [lower, higher, lIdx, hIdx] = idx1 <= idx2
          ? [fc1, fc2, idx1, idx2]
          : [fc2, fc1, idx2, idx1];
        const key = `${lower}__${higher}`;

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            key,
            title: `${this.resolveConfigName(lower)}  ↔  ${this.resolveConfigName(higher)}`,
            rows: [],
            _sortIdx1: lIdx,
            _sortIdx2: hIdx
          });
        }

        groupMap.get(key).rows.push(record);
      }

      return Array.from(groupMap.values())
        .map(group => ({
          ...group,
          rows: [...group.rows].sort((a, b) => (a.index ?? Infinity) - (b.index ?? Infinity))
        }))
        .sort((a, b) =>
          a._sortIdx1 !== b._sortIdx1
            ? a._sortIdx1 - b._sortIdx1
            : a._sortIdx2 - b._sortIdx2
        );
    }
  },

  watch: {
    /** Reload data when collection changes */
    collection(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.resetState();
        this.loadData();
      }
    }
  },

  mounted() {
    if (this.collection) {
      this.loadData();
    }
  },

  beforeDestroy() {
    this.resetState();
    this.selectedDate = null;
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchAnnexureData']),
    /**
     * Format header text for display.
     * Replaces any fileConfig IDs found in the header with their
     * human-readable names, then delegates to the shared util.
     * @param {string} text
     * @returns {string}
     */
    formatHeaderDisplay(text) {
      let result = text || '';
      // Replace fileConfig IDs with resolved names
      for (const [id, name] of Object.entries(this.fileConfigNameMap)) {
        if (result.includes(id)) {
          result = result.replaceAll(id, name);
        }
      }
      return formatHeaderText(result);
    },

    /** Handle Generate Excel button click. */
    handleGenerateExcel() {
      const dataFilters = this.selectedDate
        ? JSON.stringify([{ $match: { date: { $eq: this.selectedDate } } }])
        : null;
      this.$emit('generate-result', {
        tables: this.tabName ? [this.tabName] : [],
        collection: this.collection || null,
        dataFilters
      });
    },

    /** Reset component state to defaults */
    resetState() {
      this.summaryData = [];
      this.tableHeaders = [];
      this.page = 1;
      this.totalCount = 0;
      this.pageCount = 0;
    },

    /**
     * Resolve a fileConfig ID to a human-readable display name.
     * @param {string} configId
     * @returns {string}
     */
    resolveConfigName(configId) {
      if (!configId) return 'Unknown';
      return this.fileConfigNameMap[configId] || configId;
    },

    /**
     * Check if a column typically holds numeric data (for header right-alignment).
     * Inspects the first available row across all groups.
     * @param {string} header
     * @returns {boolean}
     */
    isNumericColumn(header) {
      for (const group of this.groupedTables) {
        if (group.rows.length > 0) {
          return typeof group.rows[0][header] === 'number';
        }
      }
      return false;
    },

    /**
     * Check if a value is numeric (for right-alignment).
     * @param {*} value
     * @returns {boolean}
     */
    isNumericValue(value) {
      return typeof value === 'number';
    },

    /**
     * Format a cell value for display.
     * null/undefined → em-dash; numbers → locale-formatted.
     * @param {*} value
     * @returns {string}
     */
    formatCell(value) {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'number') {
        return this.$nova?.formatNumber
          ? this.$nova.formatNumber(value)
          : value.toLocaleString();
      }
      return String(value);
    },

    /**
     * Load summary data from the V3 annexure API.
     * Applies date filter if a date is selected.
     */
    async loadData() {
      if (!this.collection) {
        console.error('[TMResultsSummaryV2]: No collection provided');
        return;
      }

      this.loading = true;

      try {
        const requestPayload = {
          page: this.collection,
          filters: this.dateFilterPayload,
          pagination: {
            page: this.page,
            limit: this.limit
          },
          version: 'v2'
        };

        const data = await this.fetchAnnexureData({
          collection: this.collection,
          ...requestPayload
        });

        this.summaryData = data.records || [];
        this.totalCount = data.totalCount || 0;
        this.page = data.page || 1;
        this.pageCount = data.totalPages || Math.ceil(this.totalCount / this.limit);

        // Build headers from API response, excluding internal fields
        const apiHeaders = data.headers || [];
        this.tableHeaders = apiHeaders.filter(h => !h.startsWith('_'));
      } catch (err) {
        console.error('[TMResultsSummaryV2]: Error loading data', err);
        this.$store.commit('sbError', err?.response?.data?.message || err?.message || 'Failed to load summary data');
        this.summaryData = [];
        this.totalCount = 0;
        this.pageCount = 0;
      } finally {
        this.loading = false;
      }
    },

    /** Called when user picks a date from the picker. */
    onDateSelected() {
      this.dateMenu = false;
      this.page = 1;
      this.loadData();
    },

    /** Clear the selected date and reload without a date filter. */
    clearDate() {
      this.selectedDate = null;
      this.page = 1;
      this.loadData();
    },

    /**
     * Handle pagination page change.
     * @param {number} newPage - The new page number
     */
    handlePageChange(newPage) {
      if (newPage >= 1 && newPage <= this.pageCount) {
        this.page = newPage;
        this.loadData();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import './TMResultsSummaryV2.scss';
</style>
